/**
 * @typedef {import('./index.js').Store<State, Getters>} Store
 */
/**
 * @template T
 * @typedef {import('./index.js').GetterFunction<T, State, Getters>} GetterFunction<T>
 */
/**
 * @template T
 * @template R
 * @typedef {import('./index.js').ActionFunction<T, R, State, Getters>} ActionFunction<T,R>
 */
/**
 * @typedef {import('./currency').Currency} Currency
 */
/**
 * @typedef HotelData
 * @property {string} propertyName
 * @property {string} fullHotelName
 * @property {string} destinationName
 * @property {string} shortDescription
 * @property {string} propertyLogo
 * @property {string} mobileLogo
 * @property {string} logoAlt
 * @property {string} propertyImage
 * @property {string} propertyImageAlt
 * @property {string} cancelPolicies
 * @property {string} welcomeText
 * @property {string} mapDirectionUrl
 * @property {string} restrictionPolicy
 * @property {Array<string>} bookingTerms
 * @property {string} id
 * @property {string} propertyRegion
 * @property {Currency} currency
 * @property {string} country
 * @property {string} state
 * @property {string} address
 * @property {string} city
 * @property {string} postalCode
 * @property {string} latitude
 * @property {string} longitude
 * @property {string} phone
 * @property {string} email
 * @property {string} liveChat
 * @property {string} checkIn
 * @property {string} checkOut
 * @property {Array<string>} earlyCheckIn
 * @property {Array<string>} lateCheckOut
 */
/**
 * @module Store:HotelData
 * @description Handles hotels information
 */
export default {
  namespaced: true,
  /** Global State
   * @typedef {Object} State
   * @property {Array<HotelData>} hotels - Represents a general or default list of hotel objects
   * @property {Array<HotelData>} specificHotels - Represents a concrete, filtered, or specialized list of hotel objects
   * @property {Array<string>} regions - Represents a list of geographical regions of hotels
   */
  /** @type {State} */
  state: {
    hotels: [],
    specificHotels: [],
    regions: []
  },
  /** Global Getters
   * @typedef {Object} Getters
   * @property {GetterFunction<function(string): HotelData>} getHotelById - Get {@link HotelData} based on an ID
   */
  /** @type {Getters} */
  getters: {
    getHotelById: (state) => (id) => state.hotels.find(h => h.id === id)
  },
  /** Mutations */
  mutations: {
    REGISTER_HOTELS( /** @type {State} */
      state, /** @type {Array<HotelData>} */
      hotels) {
      state.hotels = hotels.sort((a, b) => a.propertyName.localeCompare(b.propertyName))
    },
    REGISTER_SPECIFIC_HOTELS( /** @type {State} */
      state, /** @type {Array<HotelData>} */
      hotels) {
      state.specificHotels = hotels.sort((a, b) => a.propertyName.localeCompare(b.propertyName))
    },
    REGISTER_REGIONS( /** @type {State} */
      state, order) {
      state.hotels.forEach(hotel => {
        if (!state.regions.includes(hotel.propertyRegion)) {
          state.regions.push(hotel.propertyRegion)
        }
      })

      if (order && Object.keys(order).length > 0) {
        state.regions.sort((a, b) => {
          const indexA = order[a] ?? Number.MAX_SAFE_INTEGER
          const indexB = order[b] ?? Number.MAX_SAFE_INTEGER
          return indexA - indexB
        })
      }
    }
  },
  /** Global Actions
   * @typedef {Object} Actions
   * @property {ActionFunction<boolean,void>} toggleSidebar - Open or close sidebar
   * @property {ActionFunction<Array<Country>,void>} registerCountriesData - Register a list of {@link Country} data
   */
  /** @type {Actions} */
  actions: {
    registerHotels({
      commit
    }, { hotels, regionOrder }) {
      commit('REGISTER_HOTELS', hotels)
      commit('REGISTER_REGIONS', regionOrder)
    },
    registerSpecificHotels({
      commit
    }, hotels) {
      commit('REGISTER_SPECIFIC_HOTELS', hotels)
    }
  }
}
